import { useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import { staffApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { AdminStaff } from '../../api/types.js';
import { Modal } from '../../components/Modal.js';
import { Banner } from '../../components/Banner.js';
import { STAFF_PASSWORD_MIN_LENGTH } from '../../validation/staffLimits.js';

export interface SetPasswordModalProps {
  staff: AdminStaff;
  onClose(): void;
}

/**
 * Web-password reset for admin-role members (2026-08-02). Mirrors
 * SetPinModal exactly: the password is write-only everywhere in the
 * system — never returned by the API, never shown here; two blank fields
 * set a new one. Server contract: POST /staff/:id/password, min 12 chars
 * (mirrored via STAFF_PASSWORD_MIN_LENGTH — see
 * docs/decisions/2026-07-31-staff-limits-duplication.md).
 */
export function SetPasswordModal({ staff, onClose }: SetPasswordModalProps) {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const longEnough = password.length >= STAFF_PASSWORD_MIN_LENGTH;
  const valid = longEnough && password === confirm;

  async function submit() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await staffApi.setPassword(staff.id, { password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`${t('staff.setPassword')} — ${staff.displayName}`} onClose={onClose} busy={saving}>
      <p className="field-hint">{t('staff.passwordNeverShown')}</p>
      {error && <Banner kind="danger">{error}</Banner>}
      {done ? (
        <>
          <Banner kind="ok">{t('staff.passwordSet')}</Banner>
          <div className="modal__actions">
            <button type="button" className="btn btn--primary" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </>
      ) : (
        <>
          <label htmlFor="set-password">
            {t('staff.newPassword')}
            <input
              id="set-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <span className="field-hint">
              {t('staff.passwordMinLength')}
            </span>
            {password && !longEnough && (
              <span className="field-error">{t('staff.passwordTooShort')}</span>
            )}
          </label>
          <label htmlFor="set-password-confirm">
            {t('staff.passwordConfirm')}
            <input
              id="set-password-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm && password !== confirm && (
              <span className="field-error">{t('staff.passwordMismatch')}</span>
            )}
          </label>
          <div className="modal__actions">
            <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn--primary" disabled={!valid || saving} onClick={() => void submit()}>
              {t('staff.setPassword')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
